import React from 'react';
import { View, Text, TouchableOpacity, StyleSheet, ScrollView, ActivityIndicator } from 'react-native';
import { useTheme } from '../contexts/ThemeContext';

export default function ExamplesSidebar({ 
  allExamples, 
  selectedExamples, 
  recommendedExamples,
  onToggleExample,
  onMoveExample,
  updating
}) {
  const { colors, glass } = useTheme();
  const isSelected = (example) => selectedExamples.find(e => e.id === example.id);
  const isRecommended = (example) => recommendedExamples.find(e => e.id === example.id);
  const getExampleIndex = (example) => selectedExamples.findIndex(e => e.id === example.id);

  return (
    <View style={[styles.container, { backgroundColor: colors.secondary, borderLeftColor: colors.border }]}>
      <View style={[styles.header, { borderBottomColor: colors.border }]}>
        <Text style={[styles.title, { color: colors.text }]}>Examples</Text>
        <Text style={[styles.count, { color: colors.accent }]}>{selectedExamples.length}/6</Text>
      </View>

      {updating && (
        <View style={[styles.updatingBanner, { backgroundColor: colors.card }]}>
          <ActivityIndicator size="small" color={colors.accent} />
          <Text style={[styles.updatingText, { color: colors.text }]}>Updating proposal...</Text>
        </View>
      )}

      <ScrollView style={styles.exampleList}>
        {allExamples.map((example) => {
          const selected = isSelected(example);
          const recommended = isRecommended(example);
          const index = getExampleIndex(example);

          return (
            <TouchableOpacity
              key={example.id}
              style={[
                styles.exampleCard,
                { ...glass, backgroundColor: colors.card, borderColor: colors.border },
                selected && { borderColor: colors.accent, borderWidth: 2 }
              ]}
              onPress={() => onToggleExample(example)}
            >
              <View style={styles.exampleHeader}>
                <View style={styles.checkboxContainer}>
                  <View style={[
                    styles.checkbox, 
                    { borderColor: colors.border },
                    selected && { borderColor: colors.accent, backgroundColor: colors.accent }
                  ]}>
                    {selected && <Text style={[styles.checkmark, { color: '#fff' }]}>✓</Text>}
                  </View>
                  {selected && (
                    <View style={[styles.numberBadge, { backgroundColor: colors.accent }]}>
                      <Text style={[styles.numberText, { color: '#fff' }]}>#{index + 1}</Text>
                    </View>
                  )}
                </View>

                <View style={{flex: 1}}>
                  <View style={{flexDirection: 'row', alignItems: 'center', gap: 6}}>
                    <Text style={[styles.exampleTitle, { color: colors.text }]} numberOfLines={1}>
                      {example.title}
                    </Text>
                    {recommended && !selected && (
                      <View style={[styles.recommendedBadge, { backgroundColor: colors.accent }]}>
                        <Text style={[styles.recommendedText, { color: '#fff' }]}>AI</Text>
                      </View>
                    )}
                  </View>
                  <Text style={[styles.exampleCategory, { color: colors.accent }]}>{example.category}</Text>
                  <Text style={[styles.exampleDescription, { color: colors.textSecondary }]} numberOfLines={2}>
                    {example.description}
                  </Text>
                </View>

                {selected && (
                  <View style={styles.controls}>
                    <TouchableOpacity
                      style={[
                        styles.controlButton, 
                        { backgroundColor: colors.secondary, borderColor: colors.border },
                        index === 0 && { opacity: 0.3 }
                      ]}
                      onPress={() => onMoveExample(index, 'up')}
                      disabled={index === 0}
                    >
                      <Text style={[styles.controlButtonText, { color: colors.text }]}>↑</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={[
                        styles.controlButton, 
                        { backgroundColor: colors.secondary, borderColor: colors.border },
                        index === selectedExamples.length - 1 && { opacity: 0.3 }
                      ]}
                      onPress={() => onMoveExample(index, 'down')}
                      disabled={index === selectedExamples.length - 1}
                    >
                      <Text style={[styles.controlButtonText, { color: colors.text }]}>↓</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
            </TouchableOpacity>
          );
        })}
      </ScrollView>

      <View style={[styles.footer, { borderTopColor: colors.border, backgroundColor: colors.secondary }]}>
        <Text style={[styles.footerText, { color: colors.textSecondary }]}>
          {selectedExamples.length === 0 ? 'Select examples to include' : 
           selectedExamples.length < 6 ? 'Add more or reorder' : 
           'Maximum reached'}
        </Text>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    width: 320,
    backgroundColor: '#1a1a1a',
    borderRightWidth: 1,
    borderRightColor: '#333',
    flexDirection: 'column',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 15,
    borderBottomWidth: 1,
    borderBottomColor: '#333',
  },
  title: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  count: {
    fontSize: 14,
    color: '#4a9eff',
    fontWeight: '600',
  },
  updatingBanner: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 8,
    backgroundColor: '#1a2a3a',
    gap: 8,
  },
  updatingText: {
    color: '#4a9eff',
    fontSize: 12,
    fontWeight: '600',
  },
  exampleList: {
    flex: 1,
  },
  exampleCard: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: '#2a2a2a',
  },
  exampleCardSelected: {
    backgroundColor: '#0f1a2a',
    borderLeftWidth: 3,
    borderLeftColor: '#4a9eff',
  },
  exampleHeader: {
    flexDirection: 'row',
    gap: 10,
  },
  checkboxContainer: {
    alignItems: 'center',
    gap: 4,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 2,
    borderColor: '#666',
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxSelected: {
    backgroundColor: '#4a9eff',
    borderColor: '#4a9eff',
  },
  checkmark: {
    color: '#fff',
    fontSize: 12,
    fontWeight: 'bold',
  },
  numberBadge: {
    backgroundColor: '#4a9eff',
    borderRadius: 10,
    paddingHorizontal: 6,
    paddingVertical: 2,
  },
  numberText: {
    color: '#fff',
    fontSize: 10,
    fontWeight: 'bold',
  },
  exampleTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#fff',
  },
  exampleCategory: {
    fontSize: 10,
    color: '#4a9eff',
    marginTop: 2,
  },
  exampleDescription: {
    fontSize: 11,
    color: '#aaa',
    marginTop: 4,
    lineHeight: 14,
  },
  recommendedBadge: {
    backgroundColor: '#6a5aff',
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 8,
  },
  recommendedText: {
    color: '#fff',
    fontSize: 9,
    fontWeight: '600',
  },
  controls: {
    flexDirection: 'column',
    gap: 4,
  },
  controlButton: {
    width: 28,
    height: 28,
    backgroundColor: '#333',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  controlButtonDisabled: {
    opacity: 0.3,
  },
  controlButtonText: {
    color: '#fff',
    fontSize: 14,
    fontWeight: 'bold',
  },
  footer: {
    padding: 12,
    borderTopWidth: 1,
    borderTopColor: '#333',
    alignItems: 'center',
  },
  footerText: {
    fontSize: 11,
    color: '#666',
    textAlign: 'center',
  },
});
